import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Vote, 
  MessageCircle, 
  Clock, 
  MapPin, 
  Star, 
  Heart,
  ThumbsUp,
  ThumbsDown,
  Eye,
  Music,
  Camera,
  X,
  Sparkles
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { pb } from '@/lib/pocketbase';

interface InteractiveElement {
  id: string;
  type: 'poll' | 'question' | 'rating' | 'location' | 'music' | 'mood';
  data: any;
}

interface StoryInteractiveFeaturesProps {
  onAddElement: (element: InteractiveElement) => void;
  elements: InteractiveElement[];
  onRemoveElement: (id: string) => void;
}

const StoryInteractiveFeatures = ({ onAddElement, elements, onRemoveElement }: StoryInteractiveFeaturesProps) => {
  const [selectedType, setSelectedType] = useState<string>('');
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [questionText, setQuestionText] = useState('');
  const [locationText, setLocationText] = useState('');
  const [musicTrack, setMusicTrack] = useState('');
  const [selectedMood, setSelectedMood] = useState('');
  const { toast } = useToast();

  const featureTypes = [
    {
      id: 'poll',
      name: 'Poll',
      icon: Vote,
      description: 'Ask your audience to vote',
      color: 'bg-blue-500'
    },
    {
      id: 'question',
      name: 'Question',
      icon: MessageCircle,
      description: 'Ask an open question',
      color: 'bg-green-500'
    },
    {
      id: 'rating',
      name: 'Rating',
      icon: Star,
      description: 'Let people rate your experience',
      color: 'bg-yellow-500'
    },
    {
      id: 'location',
      name: 'Location',
      icon: MapPin,
      description: 'Share where you are',
      color: 'bg-red-500'
    },
    {
      id: 'music',
      name: 'Music',
      icon: Music,
      description: 'Add a music mood',
      color: 'bg-purple-500'
    },
    {
      id: 'mood',
      name: 'Mood',
      icon: Heart,
      description: 'Express your mood',
      color: 'bg-pink-500'
    }
  ];

  const moods = [
    { emoji: '😊', name: 'Happy' },
    { emoji: '😍', name: 'Excited' },
    { emoji: '😎', name: 'Cool' },
    { emoji: '🤔', name: 'Thoughtful' },
    { emoji: '😇', name: 'Peaceful' },
    { emoji: '🤗', name: 'Grateful' },
    { emoji: '🥰', name: 'Loving' },
    { emoji: '🤩', name: 'Amazing' }
  ];

  const addPollOption = () => {
    if (pollOptions.length < 4) {
      setPollOptions([...pollOptions, '']);
    }
  };

  const updatePollOption = (index: number, value: string) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  const removePollOption = (index: number) => {
    if (pollOptions.length > 2) {
      setPollOptions(pollOptions.filter((_, i) => i !== index));
    }
  };

   const handleAddElement = async () => {
     let element: InteractiveElement | null = null;

     switch (selectedType) {
       case 'poll':
         if (pollQuestion.trim() && pollOptions.filter(opt => opt.trim()).length >= 2) {
           // Create poll in PocketBase
           try {
             const pollData = await pb.collection('story_polls').create({
               question: pollQuestion.trim(),
               options: pollOptions.filter(opt => opt.trim()),
               storyId: null, // Will be set when story is created
               totalVotes: 0,
               isActive: true
             });

             element = {
               id: pollData.id,
               type: 'poll',
               data: {
                 question: pollQuestion.trim(),
                 options: pollOptions.filter(opt => opt.trim()),
                 votes: pollOptions.filter(opt => opt.trim()).map(() => 0),
                 pollId: pollData.id
               }
             };
           } catch (error) {
             console.error('Error creating poll:', error);
             toast({
               title: "Error",
               description: "Failed to create poll. Please try again.",
               variant: "destructive"
             });
             return;
           }
         }
         break;
      case 'question':
        if (questionText.trim()) {
          element = {
            id: Date.now().toString(),
            type: 'question',
            data: {
              question: questionText.trim(),
              responses: []
            }
          };
        }
        break;
       case 'rating':
         try {
           const ratingData = await pb.collection('story_ratings').create({
             storyId: null, // Will be set when story is created
             averageRating: 0,
             totalRatings: 0,
             ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
           });

           element = {
             id: ratingData.id,
             type: 'rating',
             data: {
               averageRating: 0,
               totalVotes: 0,
               ratings: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
               ratingId: ratingData.id
             }
           };
         } catch (error) {
           console.error('Error creating rating:', error);
           toast({
             title: "Error",
             description: "Failed to create rating system. Please try again.",
             variant: "destructive"
           });
           return;
         }
         break;
      case 'location':
        if (locationText.trim()) {
          element = {
            id: Date.now().toString(),
            type: 'location',
            data: {
              location: locationText.trim(),
              pinned: true
            }
          };
        }
        break;
       case 'music':
         if (musicTrack.trim()) {
           try {
             const soundData = await pb.collection('story_sound_interactions').create({
               storyId: null, // Will be set when story is created
               soundType: 'background_music',
               soundSource: musicTrack.trim(),
               isAutoPlay: false,
               volume: 0.5,
               startTime: 0,
               endTime: null
             });

             element = {
               id: soundData.id,
               type: 'music',
               data: {
                 track: musicTrack.trim(),
                 isPlaying: false,
                 soundId: soundData.id
               }
             };
           } catch (error) {
             console.error('Error creating sound interaction:', error);
             toast({
               title: "Error",
               description: "Failed to add music. Please try again.",
               variant: "destructive"
             });
             return;
           }
         }
         break;
      case 'mood':
        if (selectedMood) {
          const mood = moods.find(m => m.name === selectedMood);
          element = {
            id: Date.now().toString(),
            type: 'mood',
            data: {
              mood: selectedMood,
              emoji: mood?.emoji || '😊'
            }
          };
        }
        break;
    }

    if (element) {
      onAddElement(element);
      // Reset form
      setPollQuestion('');
      setPollOptions(['', '']);
      setQuestionText('');
      setLocationText('');
      setMusicTrack('');
      setSelectedMood('');
      setSelectedType('');
      toast({
        title: "Element Added!",
        description: `${featureTypes.find(f => f.id === selectedType)?.name} has been added to your story.`
      });
    }
  };

  const renderElementPreview = (element: InteractiveElement) => {
    switch (element.type) {
      case 'poll':
        return (
          <Card className="w-full max-w-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Vote className="h-4 w-4 text-blue-500" />
                {element.data.question}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {element.data.options.map((option: string, index: number) => (
                <div key={index} className="flex items-center justify-between p-2 border rounded hover:bg-gray-50">
                  <span className="text-sm">{option}</span>
                  <Badge variant="outline">{element.data.votes[index]}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        );
      case 'question':
        return (
          <Card className="w-full max-w-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-green-500" />
                Question
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700">{element.data.question}</p>
            </CardContent>
          </Card>
        );
      case 'rating':
        return (
          <Card className="w-full max-w-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Star className="h-4 w-4 text-yellow-500" />
                Rate this experience
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="h-5 w-5 text-gray-300 hover:text-yellow-500 cursor-pointer" />
                ))}
              </div>
            </CardContent>
          </Card>
        );
      case 'location':
        return (
          <Card className="w-full max-w-sm">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-red-500" />
                <span className="text-sm font-medium">{element.data.location}</span>
              </div>
            </CardContent>
          </Card>
        );
      case 'music':
        return (
          <Card className="w-full max-w-sm">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Music className="h-4 w-4 text-purple-500" />
                <span className="text-sm">🎵 {element.data.track}</span>
              </div>
            </CardContent>
          </Card>
        );
      case 'mood':
        return (
          <Card className="w-full max-w-sm">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <span className="text-lg">{element.data.emoji}</span>
                <span className="text-sm font-medium">Feeling {element.data.mood}</span>
              </div>
            </CardContent>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Interactive elements will be displayed here when available */}
      {elements && elements.length > 0 && (
        <div className="space-y-4">
          {elements.map((element) => (
            <div key={element.id} className="relative">
              {renderElementPreview(element)}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemoveElement(element.id)}
                className="absolute top-2 right-2 h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      
      {/* Add new interactive element interface */}
      {(!elements || elements.length === 0) && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-600">No interactive elements added yet.</p>
        </div>
      )}
    </div>
  );
};

export default StoryInteractiveFeatures;